import { useEffect, useState } from 'react'
import {
  Box, Button, CloseButton, Dialog, HStack, Input, NativeSelect, SimpleGrid, Text, VStack,
} from '@chakra-ui/react'
import { toaster } from '@/components/ui/toaster'
import { ResponsiveModal } from '../../../shared'
import { useSaveChaseSettings } from '../../../../hooks/useFollowUp'
import { extractApiMessage } from '../../../../lib/apiError'
import type { ChaseSettingsDto, ElectionType } from '../../../../types/followUp'
import { propensityLabel } from './chaseLabels'

const ELECTION_TYPES: { value: Exclude<ElectionType, 'Unknown'>; label: string }[] = [
  { value: 'General', label: 'General' },
  { value: 'Primary', label: 'Primary' },
  { value: 'Special', label: 'Special' },
  { value: 'Municipal', label: 'Municipal' },
  { value: 'Runoff', label: 'Runoff' },
  { value: 'Recall', label: 'Recall' },
  { value: 'Referendum', label: 'Referendum' },
]

const DEFAULT_RECONTACT_DAYS = 3

const toText = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n))
const toNumberOrNull = (s: string): number | null => {
  const t = s.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? Math.round(n) : null
}

export interface ChaseSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  settings: ChaseSettingsDto | undefined
}

/** CF-1 P5: the campaign's chase settings — target election + the widening thresholds. PUTs the
 *  chase settings endpoint only (never the full-campaign update). */
export function ChaseSettingsModal({ isOpen, onClose, campaignId, settings }: ChaseSettingsModalProps) {
  const [electionDate, setElectionDate] = useState('')
  const [electionType, setElectionType] = useState<ElectionType | ''>('')
  const [ideologyMin, setIdeologyMin] = useState('')
  const [ideologyMax, setIdeologyMax] = useState('')
  const [propensityMin, setPropensityMin] = useState('')
  const [activismMin, setActivismMin] = useState('')
  const [recontactDays, setRecontactDays] = useState(String(DEFAULT_RECONTACT_DAYS))
  const [serverError, setServerError] = useState<string | null>(null)

  // Each open starts from the saved settings.
  useEffect(() => {
    if (!isOpen) return
    setElectionDate(settings?.electionDate ?? '')
    setElectionType(settings?.electionType && settings.electionType !== 'Unknown' ? settings.electionType : '')
    setIdeologyMin(toText(settings?.ideologyMin))
    setIdeologyMax(toText(settings?.ideologyMax))
    setPropensityMin(toText(settings?.propensityMin))
    setActivismMin(toText(settings?.activismMin))
    setRecontactDays(String(settings?.recontactDays ?? DEFAULT_RECONTACT_DAYS))
    setServerError(null)
  }, [isOpen, settings])

  const { mutateAsync: save, isPending } = useSaveChaseSettings(campaignId)

  const handleSave = async () => {
    setServerError(null)
    try {
      await save({
        electionDate: electionDate || null,
        electionType: electionType || null,
        ideologyMin: toNumberOrNull(ideologyMin),
        ideologyMax: toNumberOrNull(ideologyMax),
        propensityMin: toNumberOrNull(propensityMin),
        activismMin: toNumberOrNull(activismMin),
        recontactDays: toNumberOrNull(recontactDays) ?? DEFAULT_RECONTACT_DAYS,
      })
      toaster.create({ title: 'Chase settings saved', type: 'success', duration: 3000 })
      onClose()
    } catch (err) {
      setServerError(extractApiMessage(err, 'Could not save chase settings.'))
    }
  }

  // Labels are plain <label htmlFor>, never Field.Root (a NativeSelect inside one is silently disabled).
  const label = (htmlFor: string, text: string) => (
    <Text asChild fontSize="xs" fontWeight="semibold" color="muted" mb={1} display="block">
      <label htmlFor={htmlFor}>{text}</label>
    </Text>
  )
  const scoreInput = (id: string, value: string, set: (v: string) => void) => (
    <Input id={id} type="number" inputMode="numeric" min={0} max={100} minH="44px"
      value={value} onChange={(e) => set(e.target.value)} />
  )

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Chase settings</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch" minW={0}>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="semibold">Target election</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                  <Box>
                    {label('chase-election-date', 'Election date')}
                    <Input id="chase-election-date" type="date" minH="44px" value={electionDate}
                      onChange={(e) => setElectionDate(e.target.value)} />
                  </Box>
                  <Box>
                    {label('chase-election-type', 'Election type')}
                    <NativeSelect.Root>
                      <NativeSelect.Field id="chase-election-type" minH="44px" value={electionType}
                        onChange={(e) => setElectionType(e.target.value as ElectionType | '')}>
                        <option value="">Not set</option>
                        {ELECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Box>
                </SimpleGrid>
                <Text fontSize="xs" color="muted">People who have voted in this election drop off the chase.</Text>
              </VStack>

              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="semibold">Likely supporters (when widening)</Text>
                <Text fontSize="xs" color="muted">Scores run 0–100. Leave a box empty for no limit.</Text>
                <SimpleGrid columns={2} gap={3}>
                  <Box>
                    {label('chase-ideology-min', 'Ideology min')}
                    {scoreInput('chase-ideology-min', ideologyMin, setIdeologyMin)}
                  </Box>
                  <Box>
                    {label('chase-ideology-max', 'Ideology max')}
                    {scoreInput('chase-ideology-max', ideologyMax, setIdeologyMax)}
                  </Box>
                </SimpleGrid>
                <Box>
                  {label('chase-propensity-min', `${propensityLabel(electionType || null)} (min)`)}
                  {scoreInput('chase-propensity-min', propensityMin, setPropensityMin)}
                </Box>
                <Box>
                  {label('chase-activism-min', 'Activism (min, optional)')}
                  {scoreInput('chase-activism-min', activismMin, setActivismMin)}
                </Box>
              </VStack>

              <Box>
                {label('chase-recontact-days', 'Skip people contacted in the last N days')}
                <Input id="chase-recontact-days" type="number" inputMode="numeric" min={0} minH="44px"
                  value={recontactDays} onChange={(e) => setRecontactDays(e.target.value)} />
              </Box>

              {!!serverError && <Text fontSize="sm" color="danger.fg" role="alert">{serverError}</Text>}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <HStack gap={3} justify="flex-end" w="100%">
              <Button variant="ghost" minH="44px" onClick={onClose}>Cancel</Button>
              <Button colorPalette="brand" minH="44px" loading={isPending} onClick={handleSave}>Save</Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
